import React, { useCallback, useMemo, useState } from 'react';
import {
  Drawer,
  List,
  notification,
} from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import AddDrawer from '../../../common/addDrawer';
import OnTraccrButtonDropdown from '../../../common/buttons/OnTraccrButtonDropdown';
import OnTraccrButton from '../../../common/buttons/OnTraccrButton';

import CostCodeEditView from '../../../costcodes/CostCodeEditView';
import useProjectGlobalCostcodes from '../../../costcodes/ProjectGlobalCostcodes';

import {
  condenseTemplatePhasesAndCostcodes,
  formatCustomFieldCreatePayload,
} from '../../../helpers/costcodeHelpers';
import { getHighestNumber, safeExtractFromObject } from '../../../helpers/helpers';

import ProjectAddGlobalCostCodeDrawer from './ProjectAddGlobalCostCodeDrawer';

const CONFLICT_NOTIFICATION_KEY = 'COST_CODE_CONFLICT_NOTIFICATION';

/**
 * Detects conflicts between new cost codes and existing chosen cost codes
 * @param {Array} newCostcodes - Array of cost codes being added
 * @param {Array} existingCostcodes - Array of existing cost codes (chosen)
 * @returns {{ hasConflict: boolean, nameConflicts: string[], codeConflicts: string[] }}
 */
const detectConflicts = (newCostcodes, existingCostcodes) => {
  const existingNames = new Set(existingCostcodes.map((cc) => cc.name ? cc.name.trim().toLowerCase() : cc.name));
  const existingCodes = new Set(existingCostcodes.map((cc) => cc.code ? cc.code.trim().toLowerCase() : cc.code));

  const nameConflicts = [];
  const codeConflicts = [];

  newCostcodes.forEach((cc) => {
    const nameKey = (cc.name || '').trim().toLowerCase();
    if (nameKey && existingNames.has(nameKey)) {
      nameConflicts.push(cc.name);
    }
    const codeKey = (cc.code || '').trim().toLowerCase();
    if (codeKey && existingCodes.has(codeKey)) {
      codeConflicts.push(cc.code);
    }
  });

  return {
    hasConflict: nameConflicts.length > 0 || codeConflicts.length > 0,
    nameConflicts,
    codeConflicts,
  };
};

const showConflictNotification = (nameConflicts, codeConflicts) => {
  const description = (
    <div>
      The following conflicts were found with existing cost codes:
      <br />
      <br />
      {nameConflicts.length > 0 && (
        <div>
          Name Conflicts:
          <p style={{ color: 'red' }}>{nameConflicts.join(', ')}</p>
          <br />
        </div>
      )}
      {codeConflicts.length > 0 && (
        <div>
          Code Conflicts:
          <p style={{ color: 'red' }}>{codeConflicts.join(', ')}</p>
          <br />
        </div>
      )}
      Please resolve these conflicts before adding.
    </div>
  );

  notification.open({
    key: CONFLICT_NOTIFICATION_KEY,
    message: 'Cost Code Conflict',
    description,
    duration: 0,
    icon: <ExclamationCircleOutlined style={{ color: 'red' }} />,
  });
};

const MENU_ITEMS = [
  { key: 'new', label: 'Add new cost code' },
  { key: 'existing', label: 'Add existing cost code' },
  { key: 'template', label: 'Apply costing template' },
];

export default function AddCostCodeDropdown({
  divisionId,
  onApplyTemplate,
  onCostcodeAdd,
  chosen = [],
  isTemplate = false,
}) {
  const { t } = useTranslation();
  const [showTemplateDrawer, setShowTemplateDrawer] = useState(false);
  const [showNewCostCodeDrawer, setShowNewCostCodeDrawer] = useState(false);
  const [showGlobalCostCodeDrawer, setShowGlobalCostCodeDrawer] = useState(false);
  const [errors, setErrors] = useState({});
  const [customFields, setCustomFields] = useState([]);

  const templates = useSelector((state) => state.costcodes.templates);
  const settings = useSelector((state) => safeExtractFromObject(state, 'settings.company.settings'));

  const { globalCostcodes } = useProjectGlobalCostcodes(showTemplateDrawer);

  const { suggestNumbers } = settings || {};

  const ourTemplates = useMemo(() => (
    templates ? templates.filter((template) => template.divisionId === divisionId) : []
  ), [divisionId, templates]);

  const globalCodes = useMemo(() => (
    new Set(globalCostcodes.map((cc) => cc.id))
  ), [globalCostcodes]);

  const menuItems = useMemo(() => {
    if (isTemplate || chosen?.length > 0) {
      return MENU_ITEMS.filter((item) => item.key !== 'template');
    }
    return MENU_ITEMS;
  }, [isTemplate, chosen]);

  const initialCode = useMemo(() => (
    suggestNumbers ? (getHighestNumber(chosen, 'code') + 1).toString() : null
  ), [suggestNumbers, chosen]);

  const handleMenuClick = useCallback(({ key }) => {
    if (key === 'template') {
      setShowTemplateDrawer(true);
    } else if (key === 'new') {
      setShowNewCostCodeDrawer(true);
    } else if (key === 'existing') {
      setShowGlobalCostCodeDrawer(true);
    }
  }, []);

  const onCloseDrawer = useCallback(() => {
    setShowTemplateDrawer(false);
  }, []);

  const onCloseNewCostCodeDrawer = useCallback((values) => {
    if (values) {
      const payload = formatCustomFieldCreatePayload({
        payload: values,
        customFields,
      });

      if (payload?.error || !payload) {
        setErrors(payload?.errorMap);
        return;
      }

      const { categoryIds } = payload;
      const newPayload = { ...payload, categoryAssignments: {} };

      categoryIds?.forEach((categoryIdToAdd) => {
        const isCustomCategory = typeof categoryIdToAdd === 'string';
        newPayload.categoryAssignments[categoryIdToAdd] = {
          defaultCategoryId: isCustomCategory ? null : categoryIdToAdd,
          customCategoryId: isCustomCategory ? categoryIdToAdd : null,
        };
      });

      // Check for conflicts before adding
      const { hasConflict, nameConflicts, codeConflicts } = detectConflicts([newPayload], chosen);
      if (hasConflict) {
        showConflictNotification(nameConflicts, codeConflicts);
        return;
      }

      onCostcodeAdd([newPayload]);
    }

    setErrors({});
    setShowNewCostCodeDrawer(false);
  }, [onCostcodeAdd, customFields, chosen]);

  const onCloseGlobalCostCodeDrawer = useCallback(() => {
    setShowGlobalCostCodeDrawer(false);
  }, []);

  const onSubmitGlobalCostCodes = useCallback((values) => {
    if (values && Array.isArray(values)) {
      // Check for conflicts before adding
      const { hasConflict, nameConflicts, codeConflicts } = detectConflicts(values, chosen);
      if (hasConflict) {
        showConflictNotification(nameConflicts, codeConflicts);
        return;
      }
      const valuesWithCategoryAssignments = values.map((value) => {
        const { categoryIds } = value;
        const newPayload = { ...value, categoryAssignments: {} };
        categoryIds?.forEach((categoryIdToAdd) => {
          const isCustomCategory = typeof categoryIdToAdd === 'string';
          newPayload.categoryAssignments[categoryIdToAdd] = {
            defaultCategoryId: isCustomCategory ? null : categoryIdToAdd,
            customCategoryId: isCustomCategory ? categoryIdToAdd : null,
          };
        });
        return newPayload;
      });
      onCostcodeAdd(valuesWithCategoryAssignments);
    }
    setShowGlobalCostCodeDrawer(false);
  }, [onCostcodeAdd, chosen]);

  const CostCodeAddView = useCallback((form, formProps) => (
    <CostCodeEditView
      form={form}
      formProps={{
        key: chosen.length,
        costcodes: chosen,
        code: initialCode,
        categoryIds: [],
        ...formProps,
      }}
      isProjectAdd
      errors={errors}
      customFields={customFields}
      setCustomFields={setCustomFields}
    />
  ), [chosen, initialCode, errors, customFields]);

  const onSelectTemplate = useCallback((template) => () => {
    const {
      data: {
        phases: selectedPhases = [],
        costcodes: selectedCostcodes = [],
        phaseCostCodes: selectedPhaseCostcodes = [],
      },
    } = template;

    const condensedPhases = condenseTemplatePhasesAndCostcodes(
      selectedPhases,
      selectedPhaseCostcodes,
      selectedCostcodes,
    );

    const formattedCostcodes = selectedCostcodes.map((costcode) => {
      const {
        categoryIds,
        categoryId,
        categoryAssignments,
      } = costcode;

      let categoryAssignmentsToUse = categoryAssignments;
      if (!categoryAssignmentsToUse) {
        categoryAssignmentsToUse = {
          [categoryId]: {
            defaultCategoryId: categoryId,
            hours: costcode.hours,
            days: costcode.days,
            estimatedCost: costcode.estimatedCost,
            estimatedQuantity: costcode.estimatedQuantity,
            units: costcode.units,
            phaseId: costcode.phaseId,
            costcodeId: costcode.id,
          },
        };
      }
      return {
        ...costcode,
        categoryAssignments: categoryAssignmentsToUse,
        categoryIds: categoryIds ?? [categoryId],
        isNew: !globalCodes.has(costcode.id),
      };
    });

    // Check for conflicts before applying template
    const {
      hasConflict, nameConflicts, codeConflicts,
    } = detectConflicts(formattedCostcodes, chosen);
    if (hasConflict) {
      showConflictNotification(nameConflicts, codeConflicts);
      return;
    }

    onApplyTemplate({
      costcodes: formattedCostcodes,
      phases: condensedPhases,
    });
    setShowTemplateDrawer(false);
  }, [onApplyTemplate, globalCodes, chosen]);

  const renderTemplate = useCallback((item) => (
    <List.Item
      key={item.id}
      extra={(
        <div style={{ height: '100%' }}>
          <OnTraccrButton
            title="Apply"
            onClick={onSelectTemplate(item)}
          />
        </div>
      )}
    >
      <List.Item.Meta title={item.name} />
      <div>{item.description}</div>
    </List.Item>
  ), [onSelectTemplate]);

  return (
    <>
      <OnTraccrButtonDropdown
        buttonTitle="Add Cost Code"
        menuItems={menuItems}
        onMenuClick={handleMenuClick}
        buttonStyle={{ height: 40 }}
      />
      <Drawer
        title={`Apply Costcode Template to ${t('Project')}`}
        visible={showTemplateDrawer}
        onClose={onCloseDrawer}
        width={400}
      >
        <List
          dataSource={ourTemplates}
          itemLayout="vertical"
          locale={{ emptyText: 'No templates found for this division' }}
          renderItem={renderTemplate}
        />
      </Drawer>
      <AddDrawer
        onClose={onCloseNewCostCodeDrawer}
        visible={showNewCostCodeDrawer}
        title={`Add New Cost Code to ${t('Project')}`}
        isAdd
        formView={CostCodeAddView}
        formProps={{
          editing: true,
        }}
        width={800}
      />
      <ProjectAddGlobalCostCodeDrawer
        visible={showGlobalCostCodeDrawer}
        onClose={onCloseGlobalCostCodeDrawer}
        divisionId={divisionId}
        chosen={chosen}
        onSubmit={onSubmitGlobalCostCodes}
      />
    </>
  );
}

/* eslint-disable react/forbid-prop-types */
AddCostCodeDropdown.propTypes = {
  divisionId: PropTypes.string,
  onApplyTemplate: PropTypes.func,
  onCostcodeAdd: PropTypes.func,
  chosen: PropTypes.array,
  isTemplate: PropTypes.bool,
};

AddCostCodeDropdown.defaultProps = {
  divisionId: null,
  onApplyTemplate: () => {},
  onCostcodeAdd: () => {},
  chosen: [],
  isTemplate: false,
};
